import axios from "axios";

import constants from "../../constants";
import { Site, SiteSeo } from "../../model/site";
import { getTrafficOrigin, markTrafficOriginSent } from "../trafficOrigin";
import { TrackingIntegrations, UploadAuthorization } from "./models";

export type AiSuggestionLanguage = "pt" | "en";

/** A text the AI should write for one field; the backend turns it into the model's prompt. */
export interface AiSuggestionRequest {
  /** Which field it's for, e.g. "welcome.featuredTitle". */
  field: string;
  /** Short guidance for the model: what to write, tone, limits. */
  instruction: string;
  maxLength?: number;
  /** Panel language: the text comes back in it. */
  language: AiSuggestionLanguage;
  context?: Record<string, string | undefined>;
}

export interface Subscription {
  type: "PLUS" | "FREE";
  canceled?: boolean;
  /** Stripe subscription status (e.g. "active", "trialing", "past_due"). Legacy
   * records may not have this backfilled. */
  status?: string;
  /** When the paid period ends (ISO date): the renewal screen waits for it to move. */
  currentPeriodEnd?: string;
}

export interface ApiSiteResponse {
  site: Site;
  /** The share image the API made for this Keepo (og:image while it has none of its own in /seo) */
  shareThumb?: string;
  siteId: string;
  draft: Site;
  disabled: boolean;
  subscription?: Subscription;
  tracking?: TrackingIntegrations;
  /** Keepo com senha is on (the password itself is read apart, by its owner: getSitePassword) */
  passwordProtected?: boolean;
  /** Verificação de idade of the Keepo: on or off, and the minimum age */
  ageGate?: SiteAgeGate;
}

/** What the owner keeps on the business card of a Keepo (the rest comes from the published Keepo). */
export type BusinessCard = {
  /** The name on the card; without it, the name of the welcome (or the title of the Keepo). */
  name?: string;
  /** What the person does. */
  role?: string;
};

/** The business card as it is drawn, from the published Keepo and what the owner keeps. */
export type BusinessCardPreview = {
  name: string;
  role: string | null;
  /** The address of the Keepo: name.keepo.bio, or its own domain. */
  url: string;
  photo: string | null;
  /** The colors of the theme of the Keepo. */
  background: string;
  foreground: string;
};

export type BusinessCardData = {
  /** What the owner kept: only the fields they touched (a field they cleared comes as ""), {} when none. */
  card: BusinessCard;
  preview: BusinessCardPreview;
  /** The colors of the Keepo, for the card of the wallets. */
  colors: { background: string; foreground: string; card: string; accent: string; accentText: string };
  /** What the Keepo already has for each field, used while the owner writes nothing else: the name of its welcome, its field of work. */
  defaults: Required<BusinessCard>;
  isPlus: boolean;
  published: boolean;
  /** Keepo's accounts with Apple and Google, set up (or not yet). */
  wallets: { apple: boolean; google: boolean };
};

/**
 * "Meus Keepos" a page at a time (GET /sites?page=): published PLUS, published FREE, PLUS drafts with a name, FREE
 * drafts (oldest first in each), and what the banners need from all of the Keepos.
 */
export interface ApiSitesPage {
  items: ApiSiteResponse[];
  total: number;
  page: number;
  perPage: number;
  summary: {
    isPROUser: boolean;
    /** Any Keepo, published or draft, without an active subscription */
    hasFreeKeepo: boolean;
    /** A PLUS subscription with no Keepo made yet (the "Assinatura disponível" banner) */
    plusWithoutKeepo: ApiSiteResponse | null;
  };
}

/** A card with nothing kept used to come as a JSON array ([]): always the object the type says. */
const normalizeBusinessCard = (data: BusinessCardData): BusinessCardData => ({
  ...data,
  card: Array.isArray(data.card) ? {} : data.card || {},
});

/** Keepo com senha: visitors type the password to see the Keepo. */
export type SitePassword = { enabled: boolean; password: string | null };

/** Verificação de idade: visitors say they are at least `minAge` (13 to 25) before seeing the Keepo. */
export type SiteAgeGate = { enabled: boolean; minAge: number };

/**
 * Mirrors the backend's `Site::hasActiveSubscription()`: a site only counts as PLUS
 * when its Stripe status is active/trialing - legacy records with no backfilled
 * status fail open and are treated as active. Keeping this in sync with the backend
 * matters because it decides whether a site counts against the free Keepos limit.
 */
export const hasActiveSubscription = (subscription?: Subscription): boolean => {
  if (!subscription || subscription.type !== "PLUS") {
    return false;
  }
  if (!subscription.status) {
    return true;
  }
  return subscription.status === "active" || subscription.status === "trialing";
};

export interface MailChimpAudienceList {
  id: string;
  name: string;
}

export interface Spreadsheet {
  id: string;
  name: string;
}

export interface Area {
  width: number;
  height: number;
  x: number;
  y: number;
}

export interface CropSize {
  width: number;
  height: number;
}

export type SavedBillingField =
  | "country"
  | "name"
  | "document"
  | "postalCode"
  | "line1"
  | "number"
  | "line2"
  | "district"
  | "city"
  | "state";

export interface User {
  /** Internal id (random uuid, not public): identifies the user to the support chat. */
  id: string;
  name: string;
  givenName: string;
  email: string;
  picture: string;
  /** The account's language ("pt", "en"; uppercase when set from the Account screen): the published page's. */
  lang?: string;
  /** The account's currency (ISO 4217, "BRL") as chosen in the Account; null until then (a guess stands in). */
  currency?: string | null;
  onboardingCompleted: boolean;
  /** Let a boleto expire unpaid: the checkout doesn't offer boleto until one of theirs clears. */
  boletoBlocked?: boolean;
  /** Billing details last used at checkout (any of the user's Keepos): the next one starts with them. */
  billing?: Partial<Record<SavedBillingField, string>> | null;
  /** Phone last given at a checkout (a domain's owner), as typed. */
  phone?: string | null;
  new?: boolean;
  ref?: string;
  identities: {
    providerType: string;
    userId: string;
  }[];
  /** How many free Keepos slots the account has, how many are used, and whether one
   * more can be created - the server is the source of truth for this (only published,
   * non-PLUS Keepos consume a slot; see `Site::getFreeSitesCount` on the backend). */
  freeSitesLimit: number;
  freeSitesCount: number;
  canCreateFreeSite: boolean;
}

/** Starting a subscription paid in the editor's own checkout (card, Apple Pay, Google Pay). */
export interface SubscriptionIntentRequest {
  plan: "MONTHLY" | "ANNUALLY";
  /** The Keepo to turn PLUS; none for a new PLUS Keepo (the server creates its id). */
  siteId?: string;
  /** `document`: CPF/CNPJ (Brazil), for the tax invoice. */
  billing: { name: string; country: string; document?: string };
  /** A promotion code (Stripe applies it to the subscription). */
  coupon?: string;
}

export interface SubscriptionIntent {
  subscriptionId: string;
  /** What Stripe.js confirms the payment with; null when there's nothing to pay (e.g. a 100% coupon). */
  clientSecret: string | null;
  /** Nothing to pay now (100% coupon): confirms the saving of the card, for the next charges. */
  setupClientSecret?: string | null;
  /** The Keepo that turns PLUS once paid (the given one, or the new one's id). */
  siteId: string;
}

/** A promotion code checked against a plan (see checkCoupon); amounts in cents. */
export interface Coupon {
  code: string;
  percentOff: number | null;
  amountOff: number | null;
  /** once (first charge), repeating (durationInMonths), forever. */
  duration: "once" | "repeating" | "forever";
  durationInMonths: number | null;
  amount: number;
  discount: number;
  total: number;
}

/** A year of PLUS paid at once with Pix or boleto (annual plan, Brazil). */
export interface OneTimePaymentRequest {
  plan: "ANNUALLY";
  method: "pix" | "boleto";
  siteId?: string;
  /** A promotion code: its discount is taken off the amount. */
  coupon?: string;
  billing: {
    name: string;
    country: string;
    document: string;
    postalCode?: string;
    line1?: string;
    number?: string;
    line2?: string;
    district?: string;
    city?: string;
    state?: string;
  };
}

export interface OneTimePayment {
  paymentIntentId: string;
  method: "pix" | "boleto";
  /** The Keepo that turns PLUS (on payment for Pix; right away for boleto). */
  siteId: string;
  /** In cents. */
  amount: number;
  currency: string;
  pix?: { code: string; qrImageUrl?: string; expiresAt?: string };
  boleto?: { number: string; pdfUrl?: string; hostedUrl?: string; expiresAt?: string };
}

/** The next year of a PLUS paid once with Pix or boleto: the charge to pay (see getRenewal). */
export interface Renewal {
  siteId: string;
  /** When the PLUS paid now ends. */
  currentPeriodEnd: string;
  renewal: Omit<OneTimePayment, "siteId"> & { forPeriodEnd: string };
}

export interface HelpRequest {
  subject: string;
  message: string;
  email: string;
}

export type BillingInterval = "month" | "year";

/** A ready-made Keepo for a field of work (see model/categories), as listed for the thumbnails. */
export interface KeepoTemplateSummary {
  id: string;
  segment: string;
  specialty?: string | null;
  name: string;
  /** Image on S3. */
  thumbnail?: string | null;
  /** Base colors of its theme (background, highlight): "black", "pink"… (see TEMPLATE_COLORS). */
  colors?: string[];
}

/** A template with its content in one language: a partial Keepo (theme, layout and sections). */
export interface KeepoTemplate extends KeepoTemplateSummary {
  content: Pick<Site, "theme" | "layout" | "sections">;
}

export interface SubscriptionDetail {
  siteId: string;
  /** What this Keepo's subscription is billed with (its own customer); none for the ones paid before that. */
  billing?: Partial<Record<SavedBillingField, string>> | null;
  /** A year of PLUS paid once (renewed by hand): Pix or boleto. Absent for a Stripe subscription (card). */
  paymentType?: "pix" | "boleto";
  /** The first boleto not paid yet (the PLUS was released when it was generated). */
  awaitingPayment?: boolean;
  /** Pix/boleto: the renewal of this period is waiting to be paid. */
  renewal?: { method: "pix" | "boleto" } | null;
  currentPeriodEnd: string;
  canceledAt?: string;
  /** When the subscription started (Stripe `start_date`). */
  startedAt?: string;
  /** The plan being paid; `amount` in the smallest currency unit (cents). */
  plan?: { interval: BillingInterval; amount: number; currency: string } | null;
  /**
   * The last payment the API recorded for this Keepo, once Stripe confirmed it: `id` is the Stripe invoice /
   * payment intent id and `value` the amount charged (in the currency's unit). For analytics.
   */
  lastPurchase?: { id: string; kind: string; value: number; currency: string; paidAt: string } | null;
  /** A discount waiting for the next charge (e.g. 20 for the retention coupon). */
  pendingDiscountPercent?: number | null;
  invoices: {
    /** Stripe invoice id: the id the payment is reported with in analytics. */
    id?: string;
    status: string;
    createdAt: string;
    /** In the smallest currency unit (cents). */
    amountPaid?: number;
    /** Before discounts, in cents; with the discount's share of it when there was one. */
    amountBeforeDiscount?: number;
    discountPercent?: number | null;
    currency?: string;
    interval?: BillingInterval;
    card?: {
      last4: string;
    };
  }[];
}

class Api {
  // These two promises only exist to sequence calls (so e.g. two saveDraft/publish
  // calls in flight resolve in the order they were made). They are intentionally
  // reset to a resolved promise after every task settles - whether it succeeded or
  // failed - so a single rejection can never poison every later call on the queue.
  // The actual result (including rejections) is still returned to the caller.
  private saveDraftQueue: Promise<void> = Promise.resolve();
  private sequenceQueue: Promise<void> = Promise.resolve();

  user: Promise<User> | undefined = undefined;
  sites: ApiSiteResponse[] | undefined = undefined;

  private runSaveDraftQueue<T>(task: () => Promise<T>): Promise<T> {
    const result = this.saveDraftQueue.then(task, task);
    this.saveDraftQueue = result.then(
      () => undefined,
      () => undefined
    );
    return result;
  }

  private runSequenceQueue<T>(task: () => Promise<T>): Promise<T> {
    const result = this.sequenceQueue.then(task, task);
    this.sequenceQueue = result.then(
      () => undefined,
      () => undefined
    );
    return result;
  }

  // `discardedFiles`: files this edit uploaded or replaced (a background's image or video); the API deletes the
  // ones the saved draft and the published Keepo no longer use
  saveDraft(id: string, site: Site, discardedFiles?: string[]) {
    this.sites = undefined;
    const body = discardedFiles?.length ? { ...site, discardedFiles } : site;
    return this.runSaveDraftQueue(() => axios.put(`${constants.api}/sites/${id}/draft`, body));
  }

  publish(id: string, site: Site) {
    this.sites = undefined;
    this.user = undefined;
    return this.runSequenceQueue(() => axios.put(`${constants.api}/sites/${id}/publish`, site));
  }

  // Search and sharing settings only (PLUS): the published Keepo and the pending draft get them,
  // but unlike `publish` it never takes the draft live. A null `seo` removes them.
  saveSeo(id: string, seo: SiteSeo | null) {
    this.sites = undefined;
    return this.runSequenceQueue(() => axios.put(`${constants.api}/sites/${id}/seo`, { seo }));
  }

  clearCacheSites() {
    this.sites = undefined;
  }

  clearCacheUser() {
    this.user = undefined;
  }

  deleteDraf(id: string) {
    this.sites = undefined;
    return axios.delete(`${constants.api}/sites/${id}/draft`);
  }

  deleteSite(id: string) {
    this.sites = undefined;
    this.user = undefined;
    return axios.delete(`${constants.api}/sites/${id}`);
  }

  getSites(): Promise<ApiSiteResponse[]> {
    return this.runSequenceQueue(() => {
      if (this.sites) {
        return Promise.resolve(this.sites);
      }
      return axios.get(`${constants.api}/sites`).then(response => {
        this.sites = response.data as ApiSiteResponse[];
        return this.sites;
      });
    });
  }

  /** A page of "Meus Keepos" (always fresh: the full list above stays cached for the other screens). */
  getSitesPage(page: number, offset?: number): Promise<ApiSitesPage> {
    // `offset`: how many the screen already shows (the page starts there: the list may have moved meanwhile)
    return axios
      .get(`${constants.api}/sites`, { params: { page, offset } })
      .then(response => response.data as ApiSitesPage);
  }

  checkUrl(siteId: string, url: string) {
    return axios.post(`${constants.api}/check-url`, { siteId, url });
  }

  async getAnalytics(siteId: string) {
    const response = await axios.get(`${constants.api}/sites/${siteId}/analytics`);
    return response?.data?.summary;
  }

  disableSite(id: string) {
    this.sites = undefined;
    this.user = undefined;
    return this.runSequenceQueue(() => axios.put(`${constants.api}/sites/${id}/disable`));
  }

  enableSite(id: string) {
    this.sites = undefined;
    this.user = undefined;
    return this.runSequenceQueue(() => axios.put(`${constants.api}/sites/${id}/enable`));
  }

  /** Free photos or videos from Pexels for a background (none searched: curated photos, popular videos). */
  // `locale`: the language of the words searched (the editor's), as Pexels names it ("pt-BR")
  async pexels(
    siteId: string,
    kind: "photos" | "videos",
    query: string,
    page: number,
    locale?: string,
    // the top's cover lies down; a background stands (the API's default)
    orientation?: "landscape"
  ): Promise<PexelsPage> {
    const response = await axios.get(`${constants.api}/sites/${siteId}/pexels/${kind}`, {
      params: {
        ...(query.trim() ? { query: query.trim(), ...(locale ? { locale } : {}) } : {}),
        ...(orientation ? { orientation } : {}),
        page,
      },
    });
    return response.data;
  }

  /**
   * "Criar com IA": starts building the Keepo being created (the API runs it in the queue). Errors: 400 with the
   * reason (ABOUT_TOO_SHORT, INVALID_INSTAGRAM...), 409 ALREADY_RUNNING / ALREADY_PUBLISHED, 429 RATE_LIMIT.
   */
  async startAiBuild(siteId: string, brief: AiBuildBrief): Promise<AiBuildState> {
    const response = await axios.post(`${constants.api}/sites/${siteId}/ai-build`, brief);
    return response.data;
  }

  /** How the build of the Keepo is going. */
  async getAiBuild(siteId: string): Promise<AiBuildState> {
    const response = await axios.get(`${constants.api}/sites/${siteId}/ai-build`);
    return response.data;
  }

  /**
   * Leave to send one file straight to the bucket: where it ends up (`id`) and a signed form (`url` and `fields`)
   * whose policy caps its size and fixes its type, so S3 refuses anything else. A video goes with its `size`.
   */
  async authorizeUpload(siteId: string, mimeType: string, size?: number): Promise<UploadAuthorization> {
    const response = await axios.post(`${constants.api}/sites/${siteId}/authorize-upload`, {
      mimeType,
      ...(size ? { size } : {}),
    });
    return response?.data;
  }

  /**
   * Sends the file with the signed form: its fields first, the file last (S3 ignores what comes after the file).
   * Without fields (an API not yet updated), it puts the file to the signed link, as it used to.
   */
  upload({ url, fields }: Pick<UploadAuthorization, "url" | "fields">, file: Blob, fileType?: string) {
    if (!fields) {
      return axios.put(url, file, { headers: { "Content-Type": fileType || file.type } });
    }
    const form = new FormData();
    Object.entries(fields).forEach(([name, value]) => form.append(name, value));
    form.append("file", file);
    return axios.post(url, form);
  }

  async createMailChimpFormConnection(siteId: string, authorizationCode: string) {
    const response = await axios.put(`${constants.api}/sites/${siteId}/form/connections/mailchimp`, {
      authorizationCode,
    });
    return response?.data;
  }

  async getAudienceListsMailChimpFormConnection(
    siteId: string,
    connectionId: string
  ): Promise<MailChimpAudienceList[]> {
    const response = await axios.get(
      `${constants.api}/sites/${siteId}/form/connections/${connectionId}/audience-lists`
    );
    return response?.data;
  }

  /** Checks a promotion code for a plan on Stripe: what it takes off, or INVALID_COUPON. */
  async checkCoupon(code: string, plan: "MONTHLY" | "ANNUALLY"): Promise<Coupon> {
    const response = await axios.post(`${constants.api}/subscription/coupon`, { code, plan });
    return response?.data;
  }

  /** A SetupIntent to save the new card of the Keepo's subscription (confirmed with Stripe.js). */
  async createCardSetup(siteId: string): Promise<{ clientSecret: string }> {
    const response = await axios.post(`${constants.api}/sites/${siteId}/subscription/card-setup`);
    return response?.data;
  }

  /** The saved card becomes the one the subscription is charged on (an unpaid invoice is retried with it). */
  async updatePaymentMethod(siteId: string, paymentMethodId: string): Promise<{ paidOpenInvoice: boolean }> {
    const response = await axios.put(`${constants.api}/sites/${siteId}/subscription/payment-method`, {
      paymentMethodId,
    });
    return response?.data;
  }

  /** What a plan change charges today, as Stripe will (the proration of what is left of the period); cents. */
  async previewPlanChange(siteId: string, plan: "MONTHLY" | "ANNUALLY"): Promise<{ amount: number; currency: string }> {
    const response = await axios.get(`${constants.api}/sites/${siteId}/subscription/plan/preview`, {
      params: { plan },
    });
    return response?.data;
  }

  /** Monthly ↔ yearly: yearly now (charged with the proration), monthly at the end of the paid year. */
  async changePlan(siteId: string, plan: "MONTHLY" | "ANNUALLY") {
    const response = await axios.put(`${constants.api}/sites/${siteId}/subscription/plan`, { plan });
    return response?.data;
  }

  /**
   * A yearly card subscription moves to Pix/boleto: the year already paid stays, the card stops being charged,
   * and the next year comes as a Pix/boleto (30 days before, by email). Answers when the paid year ends.
   */
  async switchToOneTime(
    siteId: string,
    request: { method: "pix" | "boleto"; billing: Record<string, string> }
  ): Promise<{ currentPeriodEnd: string }> {
    const response = await axios.put(`${constants.api}/sites/${siteId}/subscription/one-time`, request);
    this.user = undefined;
    return response?.data;
  }

  /**
   * A PLUS paid once with Pix/boleto moves to a card (saved with createCardSetup + Stripe.js) that renews by
   * itself: nothing is charged now, the first charge is when the paid period ends (`firstChargeAt`; none when it
   * had ended already: charged right away).
   */
  async switchToCard(
    siteId: string,
    request: { plan: "MONTHLY" | "ANNUALLY"; paymentMethodId: string }
  ): Promise<{ siteId: string; plan: string; status: string; firstChargeAt: string | null }> {
    const response = await axios.put(`${constants.api}/sites/${siteId}/subscription/card`, request);
    return response?.data;
  }

  /** Undoes a cancellation that hasn't taken effect yet. */
  async reactivateSubscription(siteId: string) {
    const response = await axios.post(`${constants.api}/sites/${siteId}/subscription/reactivate`);
    return response?.data;
  }

  /** The billing details on the receipts (name, address, CPF/CNPJ). */
  async updateBilling(siteId: string, billing: Record<string, string>) {
    const response = await axios.put(`${constants.api}/sites/${siteId}/billing`, billing);
    // The user brings the billing details saved: read them again next time
    this.user = undefined;
    return response?.data;
  }

  /** The renewal of a PLUS paid once: the charge waiting, or a new one when the last can't be paid anymore. */
  async getRenewal(siteId: string): Promise<Renewal> {
    const response = await axios.post(`${constants.api}/sites/${siteId}/renewal`);
    return response?.data;
  }

  /** Creates the Pix or boleto for a year of PLUS; answers with what to pay. */
  async createOneTimePayment(request: OneTimePaymentRequest): Promise<OneTimePayment> {
    const response = await axios.post(`${constants.api}/subscription/one-time`, request);
    this.user = undefined;
    return response?.data;
  }

  /** Creates the subscription waiting for payment; the Keepo turns PLUS once Stripe confirms it (webhook). */
  async createSubscriptionIntent(request: SubscriptionIntentRequest): Promise<SubscriptionIntent> {
    const response = await axios.post(`${constants.api}/subscription/intent`, request);
    this.user = undefined;
    return response?.data;
  }

  getInitialUser({ defaultLang, ref }: { defaultLang?: string; ref?: string }): Promise<User> {
    if (this.user) {
      return new Promise(resolve => {
        setTimeout(async () => resolve((await this.user) as User), 200);
      });
    }
    const trafficOrigin = getTrafficOrigin();
    const headers = trafficOrigin
      ? { "X-Keepo-Traffic-Origin": encodeURIComponent(JSON.stringify(trafficOrigin)) }
      : undefined;
    const request: Promise<User> = axios
      .get(`${constants.api}/users/me?defaultLang=${defaultLang}&ref=${ref}`, { headers })
      .then(response => {
        if (trafficOrigin) {
          markTrafficOriginSent(trafficOrigin);
        }
        return response.data;
      });
    this.user = request;
    // a read that failed isn't kept: the next one asks again (else every screen would get the same error until the page
    // is reloaded)
    request.catch(() => {
      if (this.user === request) this.user = undefined;
    });
    return request;
  }

  getUser(): Promise<User> {
    return this.getInitialUser({});
  }

  submitFeedback(value: number, message?: string) {
    return axios.post(`${constants.api}/feedbacks`, { value, message });
  }

  submitHelp(help: HelpRequest) {
    return axios.post(`${constants.api}/users-helps`, help);
  }

  async getTerms(lang: string, version: string) {
    const response = await axios.get(`${constants.staticBucket}/keepo/privacy-and-terms/${lang}.html?${version}`);
    return response.data;
  }

  completeOnboarding() {
    return Promise.all([
      axios.post(`${constants.api}/users/me/onboarding/complete`, {}),
      this.user?.then(user => (user.onboardingCompleted = true)),
    ]);
  }

  async updateCurrency(currency: string) {
    const response = await axios.put(`${constants.api}/users/me/currency`, { currency });
    // the cached user has the old currency: the next read brings the one just saved
    this.user = undefined;
    return response;
  }

  async updateLang(lang: string) {
    const response = await axios.put(`${constants.api}/users/me/lang`, { lang });
    // the cached user has the old language: the next read brings the one just saved
    this.user = undefined;
    return response;
  }

  /** Keepo com senha, as its owner sees it: on or off, and the password (they can see it again). */
  async getSitePassword(siteId: string): Promise<SitePassword> {
    const response = await axios.get(`${constants.api}/sites/${siteId}/password`);
    return response.data;
  }

  /**
   * Turns Keepo com senha on/off and/or sets the password (none: keeps the current one). Errors: 403 PLUS_REQUIRED
   * (turning it on or changing it), 422 INVALID_PASSWORD (not 4 to 64 characters).
   */
  async updateSitePassword(siteId: string, enabled: boolean, password?: string): Promise<SitePassword> {
    // The Keepos list carries `passwordProtected`: a cached one would say the old thing
    this.sites = undefined;
    const response = await this.runSequenceQueue(() =>
      axios.put(`${constants.api}/sites/${siteId}/password`, { enabled, password })
    );
    return response.data;
  }

  /** Verificação de idade, as its owner sees it: on or off, and the minimum age. */
  async getSiteAgeGate(siteId: string): Promise<SiteAgeGate> {
    const response = await axios.get(`${constants.api}/sites/${siteId}/age-gate`);
    return response.data;
  }

  /** Turns Verificação de idade on/off and sets the minimum age. Errors: 404 SITE_NOT_FOUND, 422 INVALID_AGE (not 13 to 25). */
  async updateSiteAgeGate(siteId: string, enabled: boolean, minAge: number): Promise<SiteAgeGate> {
    // The Keepos list carries `ageGate`: a cached one would say the old thing
    this.sites = undefined;
    const response = await this.runSequenceQueue(() =>
      axios.put(`${constants.api}/sites/${siteId}/age-gate`, { enabled, minAge })
    );
    return response.data;
  }

  /** The business card of a Keepo, as its owner sees it. */
  async getBusinessCard(siteId: string): Promise<BusinessCardData> {
    const response = await axios.get(`${constants.api}/sites/${siteId}/business-card`);
    return normalizeBusinessCard(response.data);
  }

  /** Saves the business card (a field left out goes back to what the Keepo has). */
  async saveBusinessCard(siteId: string, card: BusinessCard): Promise<BusinessCardData> {
    const response = await this.runSequenceQueue(() =>
      axios.put(`${constants.api}/sites/${siteId}/business-card`, card)
    );
    return normalizeBusinessCard(response.data);
  }

  /** The Apple Wallet pass of the card, to download (any published Keepo; 409 NOT_PUBLISHED, 503 WALLET_NOT_CONFIGURED). */
  async getAppleWalletPass(siteId: string): Promise<Blob> {
    const response = await axios.get(`${constants.api}/sites/${siteId}/business-card/apple`, { responseType: "blob" });
    return response.data;
  }

  /** The link that adds the card to Google Wallet (any published Keepo; 409 NOT_PUBLISHED, 503 WALLET_NOT_CONFIGURED). */
  async getGoogleWalletUrl(siteId: string): Promise<string> {
    const response = await axios.get(`${constants.api}/sites/${siteId}/business-card/google`);
    return response.data.url;
  }

  updateTrackingIntegrations(siteId: string, tracking: TrackingIntegrations) {
    this.sites = undefined;
    return this.runSequenceQueue(() => axios.put(`${constants.api}/sites/${siteId}/tracking`, tracking));
  }

  /** Templates for a field of work: those of the specialty first, then the segment's. */
  /** The templates of a field of work, with the thumbnails in the given language (Portuguese otherwise). */
  async getTemplates(segment: string, specialty: string | undefined, lang: string): Promise<KeepoTemplateSummary[]> {
    const response = await axios.get(`${constants.api}/templates`, { params: { segment, specialty, lang } });
    return response?.data || [];
  }

  /** A template's content in the given language (the backend falls back to Portuguese). */
  async getTemplate(id: string, lang: string): Promise<KeepoTemplate> {
    const response = await axios.get(`${constants.api}/templates/${id}`, { params: { lang } });
    return response?.data;
  }

  /** The template the Keepo was created from (its id, the uuid), kept on the Keepo for the template reports. */
  async saveTemplate(siteId: string, templateId: string): Promise<void> {
    await axios.put(`${constants.api}/sites/${siteId}/template`, { templateId });
  }

  async getSubscriptionDetail(siteId: string): Promise<SubscriptionDetail> {
    const response = await axios.get(`${constants.api}/sites/${siteId}/subscription`);
    return response?.data;
  }

  /** Whether the NPS survey should be shown to the logged user now (the rules live in the API). */
  async getNpsEligibility(): Promise<boolean> {
    const response = await axios.get(`${constants.api}/users/me/nps`);
    return !!response?.data?.eligible;
  }

  /** Saves a step of the NPS survey (upserted by `surveyId`). */
  async saveNps(payload: object): Promise<void> {
    await axios.post(`${constants.api}/users/me/nps`, payload);
  }

  /** Saves the account deletion flow's answers (upserted by `surveyId`). */
  async saveAccountDeletionSurvey(payload: object): Promise<void> {
    await axios.post(`${constants.api}/users/me/deletion-survey`, payload);
  }

  /**
   * Deletes the logged user's account (in the background: the Keepos go offline and the login stops
   * working). Refused (409 `PLUS_SUBSCRIPTION_ACTIVE`) while a PLUS subscription isn't cancelled.
   */
  async deleteAccount(): Promise<void> {
    await axios.delete(`${constants.api}/users/me`);
    this.sites = undefined;
    this.user = undefined;
  }

  async suggestText(siteId: string, request: AiSuggestionRequest, signal?: AbortSignal): Promise<string> {
    const response = await axios.post(`${constants.api}/sites/${siteId}/ai/suggestions`, request, { signal });
    return response?.data?.text || "";
  }

  /** Saves the cancellation flow's answers (upserted by `surveyId`). */
  async saveCancellationSurvey(siteId: string, payload: object): Promise<void> {
    await axios.post(`${constants.api}/sites/${siteId}/cancellation-survey`, payload);
  }

  /** Whether the retention discount (20% off the next charge) can be offered when cancelling. */
  async getRetentionDiscount(siteId: string): Promise<{ siteId: string; eligible: boolean }> {
    const response = await axios.get(`${constants.api}/sites/${siteId}/retention-discount`);
    return response?.data;
  }

  /** Applies the retention discount to the Keepo's subscription. */
  async applyRetentionDiscount(siteId: string): Promise<{ siteId: string; applied: boolean }> {
    const response = await axios.post(`${constants.api}/sites/${siteId}/retention-discount`);
    return response?.data;
  }

  /**
   * Cancels the Keepo's PLUS subscription (`Site::cancelPremium` on the backend): within the refund
   * window it's immediate and refunded, otherwise it ends with the paid period.
   */
  /**
   * `refund` (PLUS paid once, within the 7-day right of withdrawal): "pix_refunded" (refunded, back to FREE
   * now) or "boleto_support" (nothing was cancelled: support refunds and cancels it).
   */
  async cancelSubscription(
    siteId: string
  ): Promise<{ siteId: string; canceled: boolean; refund?: "pix_refunded" | "boleto_support" | null }> {
    const response = await axios.delete(`${constants.api}/sites/${siteId}/subscription`);
    // The Keepo's plan changed: the next reads must come from the server
    this.sites = undefined;
    this.user = undefined;
    return response?.data;
  }
}

export const api = new Api();

/** A Pexels photo or video, as the API gives it (a video has its duration, a file to use and one to preview). */
export type PexelsItem = {
  id: number;
  width: number;
  height: number;
  photographer: string;
  photographerUrl: string;
  url: string;
  color?: string | null;
  alt?: string;
  thumb?: string;
  image?: string;
  duration?: number;
  poster?: string;
  file?: string;
  preview?: string;
};

export type PexelsPage = { items: PexelsItem[]; nextPage: number | null };

/** What "Criar com IA" builds the Keepo from: its Instagram (optional), the owner's words and the editor's language. */
export type AiBuildBrief = { instagram: string; about: string; language: string };

/** The steps of a build, in order. */
export const AI_BUILD_STEPS = ["instagram", "research", "writing", "photos"] as const;
export type AiBuildStep = (typeof AI_BUILD_STEPS)[number];

/** How a build is going: not started, waiting, running (and its step), done or failed (and why). */
export type AiBuildState = {
  status: "none" | "queued" | "running" | "done" | "failed";
  step?: AiBuildStep;
  error?: string;
};
